// store/admin/exam-management/store/_reducer.js
import * as types from './_actionTypes';

const initialState = {
  exams: [],
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  },
  selectedExam: null,
  examAttempts: [],
  attemptStats: null,
  examAnalytics: null,
  analyticsLoading: false,
  loading: false,
  error: null,
  success: null
};

const adminExamReducer = (state = initialState, action) => {
  switch (action.type) {
    // Get all exams
    case types.GET_ALL_EXAMS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    case types.GET_ALL_EXAMS_SUCCESS:
      return {
        ...state,
        loading: false,
        exams: action.payload.data,
        pagination: action.payload.pagination || state.pagination,
        error: null
      };
    case types.GET_ALL_EXAMS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Get exam by ID
    case types.GET_EXAM_BY_ID_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    case types.GET_EXAM_BY_ID_SUCCESS:
      return {
        ...state,
        loading: false,
        selectedExam: action.payload,
        error: null
      };
    case types.GET_EXAM_BY_ID_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Create exam
    case types.CREATE_EXAM_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null
      };
    case types.CREATE_EXAM_SUCCESS:
      return {
        ...state,
        loading: false,
        exams: [action.payload, ...state.exams],
        success: 'Exam created successfully',
        error: null
      };
    case types.CREATE_EXAM_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null
      };

    // Update exam
    case types.UPDATE_EXAM_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null
      };
    case types.UPDATE_EXAM_SUCCESS:
      return {
        ...state,
        loading: false,
        exams: state.exams.map(exam =>
          exam.id === action.payload.id ? action.payload : exam
        ),
        selectedExam: action.payload,
        success: 'Exam updated successfully',
        error: null
      };
    case types.UPDATE_EXAM_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null
      };

    // Delete exam
    case types.DELETE_EXAM_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    case types.DELETE_EXAM_SUCCESS:
      return {
        ...state,
        loading: false,
        exams: state.exams.filter(exam => exam.id !== action.payload),
        success: 'Exam deleted successfully',
        error: null
      };
    case types.DELETE_EXAM_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Toggle exam status
    case types.TOGGLE_EXAM_STATUS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    case types.TOGGLE_EXAM_STATUS_SUCCESS:
      return {
        ...state,
        loading: false,
        exams: state.exams.map(exam =>
          exam.id === action.payload.id ? action.payload : exam
        ),
        selectedExam: action.payload,
        success: 'Exam status updated successfully',
        error: null
      };
    case types.TOGGLE_EXAM_STATUS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Declare exam results
    case types.DECLARE_EXAM_RESULTS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    case types.DECLARE_EXAM_RESULTS_SUCCESS:
      return {
        ...state,
        loading: false,
        exams: state.exams.map(exam =>
          exam.id === action.payload.id ? action.payload : exam
        ),
        selectedExam: action.payload,
        success: action.payload.results_declared 
          ? 'Results declared successfully' 
          : 'Results undeclared successfully',
        error: null
      };
    case types.DECLARE_EXAM_RESULTS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Get exam attempts
    case types.GET_EXAM_ATTEMPTS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    case types.GET_EXAM_ATTEMPTS_SUCCESS:
      return {
        ...state,
        loading: false,
        examAttempts: action.payload.attempts,
        attemptStats: action.payload.stats,
        error: null
      };
    case types.GET_EXAM_ATTEMPTS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Get exam analytics (live)
    case types.GET_EXAM_ANALYTICS_REQUEST:
      return {
        ...state,
        analyticsLoading: true,
        error: null
      };
    case types.GET_EXAM_ANALYTICS_SUCCESS:
      return {
        ...state,
        analyticsLoading: false,
        examAnalytics: action.payload,
        error: null
      };
    case types.GET_EXAM_ANALYTICS_FAILURE:
      return {
        ...state,
        analyticsLoading: false,
        error: action.payload
      };
    case types.CLEAR_EXAM_ANALYTICS:
      return {
        ...state,
        examAnalytics: null
      };

    // Add exam questions
    case types.ADD_EXAM_QUESTIONS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    case types.ADD_EXAM_QUESTIONS_SUCCESS:
      return {
        ...state,
        loading: false,
        success: 'Questions added successfully',
        error: null
      };
    case types.ADD_EXAM_QUESTIONS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Update exam questions
    case types.UPDATE_EXAM_QUESTIONS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null
      };
    case types.UPDATE_EXAM_QUESTIONS_SUCCESS:
      return {
        ...state,
        loading: false,
        success: 'Questions updated successfully',
        error: null
      };
    case types.UPDATE_EXAM_QUESTIONS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };

    // Local state management
    case types.SET_SELECTED_EXAM:
      return {
        ...state,
        selectedExam: action.payload
      };

    case types.CLEAR_EXAM_ERROR:
      return {
        ...state,
        error: null,
        success: null
      };

    case types.RESET_EXAM_FORM:
      return {
        ...state,
        selectedExam: null,
        error: null,
        success: null
      };

    default:
      return state;
  }
};

export default adminExamReducer;